import { useCallback, useEffect, useRef, useState } from "react";
import { AgentBrainMap, COMPANY_NAMES } from "@/components/agents/AgentBrainMap";
import { AgentDetails } from "@/components/agents/AgentDetails";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Bot, Plus, Trash2, Play, X, Server as ServerIcon, Cpu } from "lucide-react";
import { apiGet, protectedPost, protectedDelete } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { StatusDot } from "@/components/common/Panel";
import { cn } from "@/lib/utils";

const WS_STYLE = {
  regen: "text-jarvis-cyan border-jarvis-cyan/40 bg-jarvis-cyan/10",
  elevate: "text-jarvis-blue border-jarvis-blue/40 bg-jarvis-blue/10",
  eibattery: "text-jarvis-purple border-jarvis-purple/40 bg-jarvis-purple/10",
  personal: "text-jarvis-amber border-jarvis-amber/40 bg-jarvis-amber/10",
  global: "text-jarvis-cyan border-jarvis-cyan/40 bg-jarvis-cyan/10",
};
const wsStyle = (id) => WS_STYLE[id] || WS_STYLE.global;
const WS_IDS = ["regen", "elevate", "eibattery", "personal"];

function CreateModal({ open, onClose, onCreated, workspace }) {
  const [form, setForm] = useState({ name: "", role: "", workspace: "regen", kind: "sub", server: "", commands: "" });
  const [parents, setParents] = useState([]);
  useEffect(() => {
    if (!open) return;
    setForm((f) => ({ ...f, workspace: workspace === "global" ? "regen" : workspace }));
    apiGet("/agents/mesh").then((g) => setParents(g.nodes.filter((a) => a.kind === "main"))).catch(() => setParents([]));
  }, [open, workspace]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const commands = form.commands.split(",").map((c) => c.trim()).filter(Boolean);
      await protectedPost("/agents", {
        name: form.name.trim(),
        role: form.role.trim() || "Custom Agent",
        workspace: form.workspace,
        kind: form.kind,
        parent: form.kind === "main" ? "JARVIS" : parents.find((a) => a.workspace === form.workspace)?.name || "JARVIS",
        server: form.server.trim() || null,
        commands,
      });
      toast.success(`Agent "${form.name}" created, Sir.`);
      setForm({ name: "", role: "", workspace: "regen", kind: "sub", server: "", commands: "" });
      onCreated();
      onClose();
    } catch {
      toast.error("Failed to create agent.");
    }
    setSaving(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="jv-panel w-full max-w-lg"
            data-testid="create-agent-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-jarvis-cyan/10">
              <h3 className="font-mono text-sm uppercase tracking-[0.2em] text-jarvis-cyanBright">Deploy New Agent</h3>
              <button onClick={onClose} data-testid="create-agent-close" className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {[
                { k: "name", label: "Agent Name", ph: "e.g. NEXUS", testid: "agent-name-input" },
                { k: "role", label: "Role / Description", ph: "e.g. Marketing Automation", testid: "agent-role-input" },
                { k: "server", label: "Server reference (not a connection)", ph: "Optional planning label", testid: "agent-server-input" },
                { k: "commands", label: "Configured command labels", ph: "Actual worker supports: status, healthcheck, report", testid: "agent-commands-input" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">{f.label}</label>
                  <input
                    data-testid={f.testid}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    placeholder={f.ph}
                    className="w-full bg-black/40 border border-jarvis-cyan/20 rounded-md px-3 py-2 text-sm font-code text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-jarvis-cyan/50"
                  />
                </div>
              ))}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Workspace</label>
                <div className="grid grid-cols-2 gap-2">
                  {WS_IDS.map((b) => (
                    <button
                      key={b}
                      data-testid={`agent-ws-${b}`}
                      onClick={() => setForm({ ...form, workspace: b })}
                      className={cn(
                        "font-mono text-[10px] uppercase tracking-wider py-2 rounded-md border transition-all",
                        form.workspace === b ? wsStyle(b) : "border-white/10 text-slate-500"
                      )}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Type</label>
                <div className="flex gap-2">
                  {["main", "sub"].map((k) => (
                    <button
                      key={k}
                      data-testid={`agent-kind-${k}`}
                      onClick={() => setForm({ ...form, kind: k })}
                      className={cn(
                        "flex-1 font-mono text-[11px] uppercase tracking-wider py-2 rounded-md border transition-all",
                        form.kind === k ? "border-jarvis-cyan/50 text-jarvis-cyanBright bg-jarvis-cyan/10" : "border-white/10 text-slate-500"
                      )}
                    >
                      {k === "main" ? "Main Agent" : "Subagent"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-jarvis-cyan/10 flex justify-end gap-2">
              <button onClick={onClose} data-testid="create-agent-cancel" className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded-md border border-white/10 text-slate-400 hover:text-white">Cancel</button>
              <button
                onClick={submit}
                disabled={saving || !form.name.trim()}
                data-testid="create-agent-submit"
                className="font-mono text-[11px] uppercase tracking-wider px-4 py-2 rounded-md bg-jarvis-cyan/15 border border-jarvis-cyan/40 text-jarvis-cyan hover:bg-jarvis-cyan/25 disabled:opacity-40"
              >
                Deploy Agent
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [graph, setGraph] = useState(null);
  const [error, setError] = useState(false);
  const [view, setView] = useState("map");
  const [selected, setSelected] = useState(null);
  const [pendingAgent, setPendingAgent] = useState(null);
  const [open, setOpen] = useState(false);
  const { workspace, setWorkspace } = useWorkspace();
  const request = useRef(0);

  const load = useCallback(async () => {
    const sequence = ++request.current;
    try {
      const [records, mesh] = await Promise.all([apiGet(`/agents?workspace=${workspace}`), apiGet(`/agents/mesh?workspace=${workspace}`)]);
      if (sequence !== request.current) return;
      setAgents(records); setGraph(mesh); setError(false);
    } catch { if (sequence === request.current) setError(true); }
  }, [workspace]);
  useEffect(() => {
    setSelected(null);
    load();
    const id = setInterval(load, 5000);
    const invalidate = () => { request.current++; };
    return () => { clearInterval(id); invalidate(); };
  }, [workspace, load]);
  const currentGraph = graph?.workspace === workspace ? graph : null;
  const selectedAgent = currentGraph?.nodes.find((a) => a.id === selected);
  const inspect = (id, company) => {
    if (currentGraph?.nodes.some((a) => a.id === id)) setSelected(id);
    else if (company && company !== workspace) { setSelected(null); setWorkspace(company); }
  };

  const openWorker = (agent) => {
    setPendingAgent(agent.id); setWorkspace(agent.workspace); setView("map");
  };
  useEffect(() => {
    if (pendingAgent && graph?.workspace === workspace && graph.nodes.some((a) => a.id === pendingAgent)) {
      setSelected(pendingAgent); setPendingAgent(null);
    }
  }, [graph, workspace, pendingAgent]);

  const remove = async (agent) => {
    try {
      await protectedDelete(`/agents/${agent.id}`);
      setSelected(null);
      toast(`Agent "${agent.name}" decommissioned.`);
      load();
    } catch {
      toast.error("Failed to remove agent.");
    }
  };

  return (
    <div className="agents-page h-full overflow-y-auto" data-testid="agents-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bot className="w-6 h-6 text-jarvis-cyan" />
          <div>
            <h1 className="font-mono text-xl lg:text-2xl uppercase tracking-[0.25em] font-bold text-jarvis-cyan jv-glow">Agents</h1>
            <p className="font-num text-[11px] text-slate-400 tracking-wide" data-testid="agents-record-count">{currentGraph ? currentGraph.agent_count : "—"} stored agent records · {COMPANY_NAMES[workspace]}</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="create-agent-btn"
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider px-4 py-2.5 rounded-md bg-gradient-to-r from-jarvis-cyan/20 to-jarvis-purple/20 border border-jarvis-cyan/40 text-jarvis-cyanBright hover:from-jarvis-cyan/30 hover:to-jarvis-purple/30 transition-all"
        >
          <Plus className="w-4 h-4" /> Create Agent
        </button>
      </div>

      <div className="agents-company-tabs" aria-label="Company views" data-testid="agents-company-selector">
        {Object.entries(COMPANY_NAMES).map(([id, name]) => <button key={id} data-testid={`agents-company-${id}`} aria-pressed={workspace === id} onClick={() => { setWorkspace(id); setView("map"); }}>
          {name}<b data-testid={`agents-company-count-${id}`}>{graph?.companies.find((c) => c.id === id)?.count ?? "—"}</b>
        </button>)}
      </div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="agents-view-switch"><button data-testid="agents-view-map" aria-pressed={view === "map"} onClick={() => setView("map")}>Pathway map</button><button data-testid="agents-view-roster" aria-pressed={view === "roster"} onClick={() => setView("roster")}>Agent roster</button></div>
        {workspace !== "global" && <button className="text-xs text-jarvis-cyanBright py-2" data-testid="agents-back-global" onClick={() => setWorkspace("global")}>← Global map</button>}
      </div>
      {error && <div className="text-jarvis-amber text-sm p-3 mb-2 border border-jarvis-amber/30" role="alert" data-testid="agents-load-error">Could not refresh agent records. {currentGraph ? "Showing the last snapshot." : ""}<button className="ml-3 underline" data-testid="agents-retry" onClick={load}>Retry</button></div>}
      {!currentGraph && !error && <div className="brain-empty" data-testid="agents-loading">Loading agent configuration…</div>}
      {view === "map" && currentGraph && <AgentBrainMap graph={currentGraph} onCompany={setWorkspace} onInspect={inspect} />}
      {view === "roster" && <>
      <p className="text-xs text-jarvis-amber mb-3" data-testid="agents-roster-preview">App worker status is live. Load figures are PREVIEW; remote website and wallet tools are not connected.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.map((a) => (
          <motion.div
            key={a.id}
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="jv-panel p-4"
            data-testid={`agent-tile-${a.name}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-md flex items-center justify-center border border-jarvis-cyan/20 bg-black/30">
                  <Cpu className="w-4 h-4 text-jarvis-cyan" />
                </div>
                <div>
                  <div className="font-mono text-sm font-bold text-slate-100 tracking-wider">{a.name}</div>
                  <div className="text-[11px] text-slate-500">{a.role}</div>
                </div>
              </div>
              <button onClick={() => remove(a)} data-testid={`agent-delete-${a.name}`} className="text-slate-600 hover:text-jarvis-red transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span className={cn("font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border", wsStyle(a.workspace))}>
                {a.workspace}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-white/15 text-slate-400">
                {a.kind === "main" ? "MAIN" : "SUB"}
              </span>
              <span className="flex items-center gap-1.5 font-mono text-[10px] text-slate-400">
                <StatusDot status={a.status} /> {a.status}
              </span>
              <span className="font-mono text-[10px] text-slate-500 ml-auto">{a.load}%</span>
            </div>

            {a.server && (
              <div className="flex items-center gap-1.5 font-mono text-[10px] text-slate-500 mb-3">
                <ServerIcon className="w-3 h-3 text-jarvis-blue" /> {a.server}
              </div>
            )}

            <div className="border-t border-white/8 pt-3">
              <button onClick={() => openWorker(a)} data-testid={`agent-manage-worker-${a.id}`} className="flex items-center gap-2 min-h-11 px-3 rounded border border-jarvis-cyan/30 text-jarvis-cyanBright text-xs hover:bg-jarvis-cyan/10"><Play size={13} />Worker controls & checks</button>
            </div>
          </motion.div>
        ))}
      </div>
      </>}
      <AgentDetails key={selected || "closed"} agent={selectedAgent} onClose={() => setSelected(null)} onCompany={setWorkspace} onInspect={inspect} onDelete={remove} onChanged={load} />
      <CreateModal open={open} onClose={() => setOpen(false)} onCreated={load} workspace={workspace} />
    </div>
  );
}
